import React, { useRef, useState } from 'react';
import { Plus, Trash2, Building2, ChevronDown, ChevronUp, Upload, Loader2, Play } from 'lucide-react';
import { extractDocument } from '../api';
import StagedFileRow from './StagedFileRow';

/**
 * Per-building inspection findings editor.
 *
 * When populated, the calculator rolls these up to produce PDD and Habitability
 * Density. The worst building surfaces on the impact row as `driver_building`.
 *
 * Workflow: stage one or more inspection reports → review the file list →
 * Parse individually or Parse All. Each successful parse merges its buildings
 * into the table (deduped by name; later parses overwrite earlier ones for
 * matching names).
 */
export default function BuildingsEditor({ buildings = [], onChange, onExtractedVariables, onExtractedProperty, onRecordUpload }) {
    const [expanded, setExpanded] = useState(true);
    const inputRef = useRef(null);
    const [staged, setStaged] = useState([]);
    // staged item: { id, file, status: 'staged'|'parsing'|'parsed'|'error', result, error }

    const handleFilePick = (filesList) => {
        const files = Array.from(filesList || []);
        if (!files.length) return;
        const next = files.map((f, i) => ({
            id: `${Date.now()}-${i}-${f.name}`,
            file: f,
            status: 'staged',
            result: null,
            error: null,
        }));
        setStaged(prev => [...prev, ...next]);
        if (inputRef.current) inputRef.current.value = '';  // allow re-picking same file
    };

    const updateStaged = (id, patch) => {
        setStaged(prev => prev.map(s => (s.id === id ? { ...s, ...patch } : s)));
    };

    const removeStaged = (id) => {
        setStaged(prev => prev.filter(s => s.id !== id));
    };

    const mergeBuildings = (incoming) => {
        if (!Array.isArray(incoming) || incoming.length === 0) return;
        // Dedupe by name; later parses overwrite earlier ones for matching names.
        const map = new Map();
        (buildings || []).forEach(b => map.set(b.name, b));
        incoming.forEach(b => map.set(b.name, b));
        onChange(Array.from(map.values()));
    };

    const parseOne = async (item) => {
        updateStaged(item.id, { status: 'parsing', error: null });
        try {
            const fd = new FormData();
            fd.append('file', item.file);
            const res = await extractDocument(fd);
            if (!res.ok) {
                let detail = 'Extraction failed';
                try { detail = (await res.json()).detail || detail; } catch (_) {}
                throw new Error(detail);
            }
            const result = await res.json();
            updateStaged(item.id, { status: 'parsed', result });
            if (result.is_high_trust) {
                mergeBuildings(result.buildings);
                // Property metadata (name, address, units, programs…) — auto-fills
                // the property form. User can still edit anything that came back wrong.
                if (result.property && Object.keys(result.property).some(k => result.property[k])) {
                    onExtractedProperty?.(result.property);
                }
                // Per-variable extractions (e.g. PDD, Habitability) — fills VariableRow inputs.
                if (result.extracted_variables && Object.keys(result.extracted_variables).length) {
                    onExtractedVariables?.(result.extracted_variables, item.file.name);
                }
            }
            // Audit trail entry for the saved run — records what we uploaded and
            // what came of it, regardless of high-trust outcome.
            onRecordUpload?.({
                filename: item.file.name,
                size_kb: Math.round((item.file.size || 0) / 1024),
                document_type: result.document_type,
                inferred_label: result.inferred_label,
                reasoning: result.reasoning,
                is_high_trust: result.is_high_trust,
                source: 'buildings_editor',
                contributed_variables: Object.keys(result.extracted_variables || {}),
                contributed_buildings: (result.buildings || []).map(b => b.name),
                uploaded_at: new Date().toISOString(),
            });
        } catch (e) {
            updateStaged(item.id, { status: 'error', error: e.message || 'Parse failed' });
        }
    };

    const parseAll = async () => {
        const pending = staged.filter(s => s.status === 'staged' || s.status === 'error');
        await Promise.all(pending.map(parseOne));
    };

    const totalUnits = buildings.reduce((sum, b) => sum + (b.units || 0), 0);
    const totalLS = buildings.reduce((sum, b) => sum + (b.life_safety || 0), 0);
    const stagedCount = staged.filter(s => s.status === 'staged' || s.status === 'error').length;
    const anyParsing = staged.some(s => s.status === 'parsing');

    const addBuilding = () => {
        onChange([
            ...buildings,
            {
                name: `Building ${String.fromCharCode(65 + buildings.length)}`,
                units: 0,
                life_safety: 0,
                major: 0,
                minor: 0,
                in_unit_life_safety: 0,
                ns_raw_score: null,
                items_inspected: null,
                overall_condition: '',
                notes: '',
            },
        ]);
    };

    const updateBuilding = (idx, patch) => {
        onChange(buildings.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
    };

    const removeBuilding = (idx) => onChange(buildings.filter((_, i) => i !== idx));

    return (
        <section className="mb-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 overflow-hidden">
            <button type="button" onClick={() => setExpanded(!expanded)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                <div className="flex items-center gap-2.5">
                    <Building2 size={16} className="text-slate-500" />
                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-100 tracking-tight">
                        Per-Building Inspection
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">
                        {buildings.length === 0 ? 'optional' : `${buildings.length} building${buildings.length !== 1 ? 's' : ''} · ${totalUnits} units · ${totalLS} LS findings`}
                    </span>
                </div>
                {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
            </button>

            {expanded && (
                <div className="px-4 pb-4 space-y-4">
                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                        Stage any physical inspection reports — NSPIRE, REAC, CNA, PNA, Section 504, Fire Marshal, PCR, Engineering, Draw Inspection — then click Parse. Each parsed report merges its buildings into the table.
                    </p>

                    {/* Stage zone */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <button type="button" onClick={() => inputRef.current?.click()}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border-2 border-dashed border-green-400 dark:border-green-700 text-green-700 dark:text-green-300 text-sm font-medium hover:bg-green-50 dark:hover:bg-green-950/30 transition-colors">
                            <Upload size={14} /> Stage Inspection Report{staged.length === 0 ? '' : 's'}
                        </button>
                        <input ref={inputRef} type="file" multiple accept=".pdf,.docx,.xlsx,.xls,image/*" className="hidden"
                               onChange={(e) => handleFilePick(e.target.files)} />

                        {stagedCount > 1 && (
                            <button type="button" onClick={parseAll} disabled={anyParsing}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium shadow-sm disabled:opacity-50 transition-colors">
                                {anyParsing ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                                Parse All ({stagedCount})
                            </button>
                        )}
                    </div>

                    {/* Staged file list */}
                    {staged.length > 0 && (
                        <div className="space-y-2">
                            {staged.map(item => (
                                <StagedFileRow key={item.id} file={item.file} status={item.status}
                                               result={item.result} error={item.error}
                                               onParse={() => parseOne(item)}
                                               onRemove={() => removeStaged(item.id)} />
                            ))}
                        </div>
                    )}

                    {/* Buildings table */}
                    {buildings.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                    <tr className="border-b border-slate-200 dark:border-slate-800">
                                        <th className="py-2 pr-3 text-left font-medium">Building</th>
                                        <th className="py-2 pr-3 text-right font-medium">Units</th>
                                        <th className="py-2 pr-3 text-right font-medium">Life Safety</th>
                                        <th className="py-2 pr-3 text-right font-medium">In-Unit LS</th>
                                        <th className="py-2 pr-3 text-right font-medium">Major</th>
                                        <th className="py-2 pr-3 text-right font-medium">Minor</th>
                                        <th className="py-2 pr-3 text-right font-medium" title="NSPIRE / REAC raw score">NS Score</th>
                                        <th className="py-2 pr-3 text-right font-medium" title="Total items inspected at this building">Items</th>
                                        <th className="py-2 pr-3 text-left font-medium">Condition</th>
                                        <th className="py-2 pr-3 text-left font-medium">Notes</th>
                                        <th className="py-2 w-8" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {buildings.map((b, i) => (
                                        <tr key={i} className="border-b border-slate-100 dark:border-slate-800 last:border-0">
                                            <td className="py-2 pr-3">
                                                <input value={b.name || ''} onChange={(e) => updateBuilding(i, { name: e.target.value })}
                                                       className="w-32 px-2 py-1 rounded-md border border-transparent hover:border-slate-300 dark:hover:border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none bg-transparent text-slate-800 dark:text-slate-100" />
                                            </td>
                                            {['units', 'life_safety', 'in_unit_life_safety', 'major', 'minor'].map((field) => (
                                                <td key={field} className="py-2 pr-3 text-right">
                                                    <input type="number" min={0} value={b[field] || 0}
                                                           onChange={(e) => updateBuilding(i, { [field]: parseInt(e.target.value, 10) || 0 })}
                                                           className="w-16 px-2 py-1 rounded-md border border-transparent hover:border-slate-300 dark:hover:border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none bg-transparent text-right tabular-nums text-slate-800 dark:text-slate-100" />
                                                </td>
                                            ))}
                                            <td className="py-2 pr-3 text-right">
                                                <input type="number" step={0.1} value={b.ns_raw_score ?? ''}
                                                       onChange={(e) => updateBuilding(i, { ns_raw_score: e.target.value === '' ? null : Number(e.target.value) })}
                                                       className="w-16 px-2 py-1 rounded-md border border-transparent hover:border-slate-300 dark:hover:border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none bg-transparent text-right tabular-nums text-slate-800 dark:text-slate-100" />
                                            </td>
                                            <td className="py-2 pr-3 text-right">
                                                <input type="number" min={0} value={b.items_inspected ?? ''}
                                                       onChange={(e) => updateBuilding(i, { items_inspected: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                                                       className="w-16 px-2 py-1 rounded-md border border-transparent hover:border-slate-300 dark:hover:border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none bg-transparent text-right tabular-nums text-slate-800 dark:text-slate-100" />
                                            </td>
                                            <td className="py-2 pr-3">
                                                <select value={b.overall_condition || ''}
                                                        onChange={(e) => updateBuilding(i, { overall_condition: e.target.value })}
                                                        className="w-24 px-2 py-1 rounded-md border border-transparent hover:border-slate-300 dark:hover:border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none bg-transparent text-slate-800 dark:text-slate-100">
                                                    <option value="">—</option>
                                                    <option>Excellent</option>
                                                    <option>Good</option>
                                                    <option>Fair</option>
                                                    <option>Poor</option>
                                                </select>
                                            </td>
                                            <td className="py-2 pr-3">
                                                <input value={b.notes || ''} onChange={(e) => updateBuilding(i, { notes: e.target.value })}
                                                       placeholder="Inspector notes…"
                                                       title={b.notes || ''}
                                                       className="w-48 px-2 py-1 rounded-md border border-transparent hover:border-slate-300 dark:hover:border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none bg-transparent text-slate-800 dark:text-slate-100 truncate" />
                                            </td>
                                            <td className="py-2">
                                                <button onClick={() => removeBuilding(i)} className="p-1 text-slate-400 hover:text-red-600 rounded transition-colors">
                                                    <Trash2 size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <button type="button" onClick={addBuilding}
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">
                        <Plus size={14} /> Add Building Manually
                    </button>
                </div>
            )}
        </section>
    );
}
